import React, { useState } from 'react';
import { Shield, FileText, Copyright, Award, ArrowRight } from 'lucide-react';
import RegistrationModal from './RegistrationModal';

const WhatIsTrademarkSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const types = [
    {
      icon: FileText,
      title: 'Word Marks',
      description: 'Brand names, slogans, and text-based identifiers that distinguish your products or services'
    },
    {
      icon: Copyright,
      title: 'Design Marks',
      description: 'Logos, symbols, colors, and visual elements that represent your brand identity'
    },
    {
      icon: Shield,
      title: 'Service Marks',
      description: 'Identifiers specifically for services rather than physical products'
    },
    {
      icon: Award,
      title: 'Collective Marks',
      description: 'Marks used by members of a collective group or organization'
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div className="flex flex-col">
            <div className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Understanding Trademarks
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              What is a Trademark?
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>
                A trademark is a distinctive sign, symbol, word, phrase, logo, or design that identifies and distinguishes the source of goods or services of one party from those of others. It's essentially your brand's fingerprint in the marketplace.
              </p>
              <p>
                Think of iconic brands like Nike's swoosh, Apple's apple, or McDonald's golden arches. These trademarks are instantly recognizable and legally protected, preventing others from using confusingly similar marks.
              </p>
              <p>
                In the United States, trademark rights are established through either actual use in commerce or by filing an application with the United States Patent and Trademark Office (USPTO). Once registered, a trademark can last indefinitely as long as it continues to be used and properly maintained.
              </p>
              <p className="font-semibold text-black">
                A strong trademark not only protects your brand legally but also builds customer trust, brand recognition, and business value over time.
              </p>
            </div>
          </div>

          {/* Right Content - Types */}
          <div className="flex flex-col justify-between h-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 mb-6">
              <h3 className="text-2xl font-bold text-black mb-6">Types of Trademarks</h3>
              <div className="space-y-6">
                {types.map((type, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                        <type.icon className="w-6 h-6 text-[#FF6B35]" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">{type.title}</h4>
                      <p className="text-gray-600 text-sm">{type.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Small CTA - Aligned to bottom */}
            <div className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] rounded-2xl shadow-xl p-6 text-white mt-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold text-lg mb-1">Ready to Protect Your Brand?</h4>
                  <p className="text-white/90 text-sm">Start your trademark registration today</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white text-[#FF6B35] px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 whitespace-nowrap"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default WhatIsTrademarkSection;